import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CONVERSATION_KANBAN_LANE_IDS,
  CONVERSATION_KANBAN_SORT_MODES,
  type ConversationKanbanCard,
  type ConversationKanbanLaneId,
  type ConversationKanbanQuery,
} from '@troia-v3/types';
import { api } from '@/lib/api';
import { CardDensitySelector, KanbanBoard, KanbanSortSelector } from '@/shared/components/kanban';
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  PageToolbar,
  ContextSelector,
  OperatorAvatarFilter,
  ToolbarSearch,
  type OperatorAvatarFilterOperator,
} from '@/shared/components/ui';
import FinishChatDialog from '@/modules/chat/components/FinishChatDialog';
import { useConversationActions } from '@/modules/chat/hooks/useConversationActions';
import { useAuth } from '@/contexts/AuthContext';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { ConversationCardImproved } from '../components';
import type { AttendanceCardConversation } from '../components/ConversationCardImproved';
import { AttendanceFiltersComponent } from '../components/AttendanceFilters';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import {
  useAttendanceKanban,
  useProvidersDropdown,
  useAttendancePreferences
} from '../hooks';
import { AttendanceFilters as IAttendanceFilters, AttendanceAction } from '../types';

/**
 * Visual das 5 raias fixas do board (títulos/cores preservados da tela
 * client-side pré-SP4 — antigo `convertConversationsToKanban`). A ordem de
 * exibição segue `CONVERSATION_KANBAN_LANE_IDS`; os predicados das lanes
 * vivem só no backend (D1/D6 do spec SP4).
 */
const LANE_VISUALS: Record<ConversationKanbanLaneId, { title: string; color: string }> = {
  ai: { title: 'IA', color: '#9333ea' },
  queue: { title: 'Fila de Espera', color: '#f97316' },
  inService: { title: 'Em Atendimento', color: '#3b82f6' },
  transferred: { title: 'Transferidos', color: '#eab308' },
  closed: { title: 'Últimos Finalizados', color: '#22c55e' },
};

/**
 * Página de Gerenciamento de Atendimentos (Improved)
 *
 * Design superior baseado no frontend antigo com:
 * - Sidebar lateral de teams (20%)
 * - Header com métricas destacadas
 * - FilterBar horizontal sempre visível
 * - Kanban windowed (SP4): janelas de 50/lane com counts reais, busca e
 *   filtros server-side, paginação por cursor e modos de ordenação
 *
 * @example
 * Route: /chat/management
 */
export const AttendanceManagementPageImproved: React.FC = () => {
  // App context for users
  // Mesmo padrão dos boards de CRM/tickets: React Query com cache/dedupe
  // compartilhado (queryKey ['users','dropdown']) em vez do AppContext
  const { data: appUsers = [] } = useUsersDropdown();
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();

  // Attendance preferences (persisted in localStorage)
  const { sortMode, setSortMode, cardDensity, setCardDensity } = useAttendancePreferences();

  // State management
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<IAttendanceFilters>({});

  // Busca server-side do board com debounce (~300ms) — evita 1 request por
  // tecla (D9/D10 do spec SP4; substitui o filtro client-side de
  // contact.name/id/subject da versão anterior).
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ✅ Fetch dropdown data from API
  const { data: channelsData = [] } = useChannelsDropdown();
  const { data: teamsData = [] } = useTeamsDropdown();
  // ✅ Filter providers by receive_message capability (messaging providers only)
  const { data: providersData = [] } = useProvidersDropdown(['receive_message']);

  // Itens do seletor de equipe do header (mockup: .funnel "Todas as equipes")
  const teamSelectorItems = useMemo(() => ([
    { id: 'all', name: 'Todas as equipes' },
    ...teamsData.map((team) => ({ id: team.id, name: team.name })),
  ]), [teamsData]);

  // ✅ Transform channels data to filter format
  const channelsForFilter = useMemo(() => {
    return channelsData.map(channel => ({
      id: channel.id,
      name: channel.name
    }));
  }, [channelsData]);

  // ✅ Transform teams data to filter format
  const teamsForFilter = useMemo(() => {
    return teamsData.map(team => ({
      id: team.id,
      name: team.name
    }));
  }, [teamsData]);

  // ✅ Transform providers data to filter format
  const providersForFilter = useMemo(() => {
    return providersData.map(provider => ({
      id: provider.id,
      name: provider.name,
      logo: provider.logo
    }));
  }, [providersData]);

  // Transform AppContext users (UserResponse) to the shared OperatorAvatarFilter shape
  const users: OperatorAvatarFilterOperator[] = useMemo(() => {
    return appUsers.map(user => ({
      id: user.id,
      name: user.name,
      avatar: user.avatar
    }));
  }, [appUsers]);

  // Filtros do board (server-side, D10): subset suportado pelo
  // ConversationKanbanQuery (channelId/assigneeId/teamId/providerId) —
  // conversationType/status NÃO existem no kanban (grupos são excluídos pelo
  // backend por paridade; status é a raia). MEMOIZADO pelas dependências
  // reais: objeto novo a cada render mudaria a identidade da queryKey e
  // recriaria o `loadMore`, desmontando o sentinel de scroll (bug real do SP1).
  const kanbanFilters = useMemo<ConversationKanbanQuery['filters']>(() => {
    const mapped: NonNullable<ConversationKanbanQuery['filters']> = {};
    if (filters.channelId) mapped.channelId = filters.channelId;
    if (filters.providerId) mapped.providerId = filters.providerId;
    const teamId = selectedTeamId || filters.teamId;
    if (teamId) mapped.teamId = teamId;
    const assigneeId = selectedUserId !== 'all' ? selectedUserId : filters.assigneeId;
    if (assigneeId) mapped.assigneeId = assigneeId;
    return Object.keys(mapped).length > 0 ? mapped : undefined;
  }, [filters, selectedTeamId, selectedUserId]);

  // Board kanban windowed (SP4): counts/busca/paginação por cursor vêm do
  // backend; `applyCardMove` faz cirurgia de cache no drag preservando o
  // scroll das lanes. Polling de 60s vive no hook (D12 — paridade com a tela
  // anterior).
  const {
    boardData,
    isLoading,
    error,
    loadMore,
    applyCardMove,
    invalidate: invalidateKanban,
    loadingColumns,
    errorColumns,
  } = useAttendanceKanban({
    search: debouncedSearch || undefined,
    filters: kanbanFilters,
    sortMode,
  });

  // Colunas das 5 raias fixas: janelas/counts reais do backend por laneId,
  // visual (títulos/cores) preservado da tela anterior.
  const kanbanColumns = useMemo(() => {
    if (!boardData) return [];
    const byId = new Map(boardData.columns.map((c) => [c.columnId, c]));
    return CONVERSATION_KANBAN_LANE_IDS.map((laneId) => {
      const col = byId.get(laneId);
      return {
        id: laneId,
        title: LANE_VISUALS[laneId].title,
        color: LANE_VISUALS[laneId].color,
        items: col?.items ?? [],
        count: col?.count ?? 0,
        hasMore: Boolean(col?.nextCursor),
        loadingMore: Boolean(loadingColumns[laneId]),
        loadMoreError: Boolean(errorColumns[laneId]),
      };
    });
  }, [boardData, loadingColumns, errorColumns]);

  // Métricas do header derivadas dos counts das lanes (total matched por
  // raia no backend, não só a janela carregada — D4). Semântica preservada:
  // "ativos" = em atendimento agora (IA + humano); "atendimentos" = não
  // fechadas (ai + queue + inService particionam waiting/active).
  const { activeAttendances, totalAttendances } = useMemo(() => {
    const counts = new Map(boardData?.columns.map((c) => [c.columnId, c.count]) ?? []);
    const count = (laneId: ConversationKanbanLaneId) => counts.get(laneId) ?? 0;
    return {
      activeAttendances: count('ai') + count('inService'),
      totalAttendances: count('ai') + count('queue') + count('inService'),
    };
  }, [boardData]);

  // Alvo das ações resolvidas no nível da página (D8): close abre o
  // FinishChatDialog (reuso do fluxo do chat — o dialog é desacoplado),
  // reopen abre confirmação simples. O hook fica vinculado ao id do alvo do
  // render corrente; as mutations de `useConversationActions` já invalidam
  // ['attendance-kanban'] e ['conversations'].
  const [actionTarget, setActionTarget] = useState<{
    action: 'close' | 'reopen';
    conversation: AttendanceCardConversation;
  } | null>(null);

  const { closeConversation, isClosing, reopenConversation, isReopening } = useConversationActions({
    conversationId: actionTarget?.conversation.id ?? '',
    onCloseSuccess: () => setActionTarget(null),
    onReopenSuccess: () => setActionTarget(null),
  });

  // Handler para ações do card — attend/transfer são resolvidas dentro do
  // próprio ConversationCardImproved; aqui chegam só close/reopen (ex-stub).
  const handleAction = (action: AttendanceAction, conversation: AttendanceCardConversation) => {
    if (action === 'close' || action === 'reopen') {
      setActionTarget({ action, conversation });
    }
  };

  // Drag D7: único par válido é queue → inService (= assumir o atendimento
  // via POST /conversations/:id/start, mesmo endpoint do "Atender" do card).
  // Cirurgia otimista preserva o scroll das lanes — a lane é DERIVADA de
  // status/assignee no backend, então o card entra no cache JÁ atualizado
  // (status 'active' + assignee = user logado). Erro refaz o board
  // (invalidate desfaz a cirurgia) e mostra a mensagem da API.
  const handleCardMove = async (
    card: ConversationKanbanCard,
    fromLaneId: string,
    toLaneId: string,
  ) => {
    if (fromLaneId === toLaneId) return; // defensivo — o board só emite cross-column
    if (!(fromLaneId === 'queue' && toLaneId === 'inService')) {
      toast('Use as ações do card para fechar/reabrir.', { icon: 'ℹ️' });
      return;
    }

    const user = authState.user;
    const optimisticCard: ConversationKanbanCard = {
      ...card,
      status: 'active',
      ...(user
        ? {
            assigneeId: user.id,
            assignee: {
              id: user.id,
              name: `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`,
            },
          }
        : {}),
    };
    applyCardMove(optimisticCard, fromLaneId, toLaneId);

    try {
      await api.post(`/conversations/${card.id}/start`);
      toast.success('Atendimento iniciado!');
      // Mesmas invalidations do "Atender" do card, MENOS o board (a cirurgia
      // local preserva o scroll) e sem navegar pro chat (drag = só assumir).
      queryClient.invalidateQueries({ queryKey: ['conversation', card.id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    } catch (err) {
      invalidateKanban();
      const apiMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(apiMessage || 'Erro ao iniciar atendimento');
    }
  };

  return (
    <div className="h-full flex" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Main Content — equipe agora vive no header (mockup: SEM sidebar interna) */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar 3 zonas do mockup attendance-management: seletor de equipe +
            métricas | busca | Filtros + Ordenar + operadores (sem primário) */}
        <PageToolbar
          chain="at-tb"
          data-testid="attendance-toolbar"
          left={
            <>
              <ContextSelector
                items={teamSelectorItems}
                selectedId={selectedTeamId ?? 'all'}
                onSelect={(id) => setSelectedTeamId(id === 'all' ? null : id)}
                placeholder="Todas as equipes"
                data-testid="attendance-team-selector"
              />
              {/* `.metric` pills: número roxo/escuro + rótulo (somem ≤1150) */}
              <span
                data-testid="attendance-metric-active"
                className="at-metric inline-flex items-center gap-1.5 rounded-full font-semibold flex-none"
                style={{
                  height: 26,
                  padding: '0 10px',
                  fontSize: 12,
                  backgroundColor: 'var(--theme-surface-sunken, rgba(148,163,184,.16))',
                  color: 'var(--theme-muted-text)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <b style={{ color: 'var(--theme-primary)', fontSize: 13 }}>{activeAttendances}</b>
                ativos
              </span>
              <span
                data-testid="attendance-metric-total"
                className="at-metric inline-flex items-center gap-1.5 rounded-full font-semibold flex-none"
                style={{
                  height: 26,
                  padding: '0 10px',
                  fontSize: 12,
                  backgroundColor: 'var(--theme-surface-sunken, rgba(148,163,184,.16))',
                  color: 'var(--theme-muted-text)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <b style={{ color: 'var(--theme-text)', fontSize: 13 }}>{totalAttendances}</b>
                atendimentos
              </span>
            </>
          }
          center={
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar por nome ou telefone…"
              shrinkClass="ds-search hidden sm:flex"
              data-testid="attendance-search-input"
            />
          }
          right={
            <>
              <AttendanceFiltersComponent
                variant="toolbar"
                filters={filters}
                onFiltersChange={setFilters}
                channels={channelsForFilter}
                teams={teamsForFilter}
                providers={providersForFilter}
              />
              <KanbanSortSelector
                value={sortMode}
                onChange={setSortMode}
                modes={CONVERSATION_KANBAN_SORT_MODES}
              />
              {/* Tamanho do card. Atendimento não tem toggle de visualização
                  (é sempre kanban), então o Tamanho entra logo depois do Ordenar —
                  spec § 3. Legenda colapsa no padrão do DS (hidden 2xl:@tb:inline,
                  via ViewToggle). */}
              <span className="inline-flex flex-none">
                <CardDensitySelector
                  value={cardDensity}
                  onChange={setCardDensity}
                  data-testid="attendance-card-density"
                />
              </span>
              <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />
              {/* F12-15: filtro de operadores shared (attendance mantém a posição
                  por último, SEM CTA primário — ação de atender é por card) */}
              <OperatorAvatarFilter
                operators={users}
                selectedId={selectedUserId === 'all' ? null : selectedUserId}
                onSelect={(id) => setSelectedUserId(id ?? 'all')}
                loading={isLoading}
                data-testid="attendance-operators"
              />
            </>
          }
          mobile={
            /* Mockup attendance-management mobile: equipe+sort (appbar do mockup —
               realocados pra 1ª linha, shell não injeta título) e `.m-sub` = busca
               sunken + chip Operadores + Filtros na 2ª linha */
            <div className="w-full flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="flex-none">
                  <ContextSelector
                    items={teamSelectorItems}
                    selectedId={selectedTeamId ?? 'all'}
                    onSelect={(id) => setSelectedTeamId(id === 'all' ? null : id)}
                    placeholder="Todas as equipes"
                    data-testid="attendance-team-selector-mobile"
                  />
                </span>
                <span className="flex-1" />
                {/* F60: rótulo fixo "Ordenar" (padrão CRM/app.html) */}
                <KanbanSortSelector
                  value={sortMode}
                  onChange={setSortMode}
                  modes={CONVERSATION_KANBAN_SORT_MODES}
                />
                {/* Tamanho do card ao lado do Ordenar (mesma dupla do desktop).
                    A legenda fica oculta aqui por padrão (base `hidden` — só
                    aparece em ≥2xl): esta faixa é `flex` SEM overflow próprio,
                    então um controle largo comprimiria o seletor de equipe. */}
                <span className="inline-flex flex-none">
                  <CardDensitySelector
                    value={cardDensity}
                    onChange={setCardDensity}
                    data-testid="attendance-card-density-mobile"
                  />
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <ToolbarSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Buscar por nome ou telefone…"
                    data-testid="attendance-search-input-mobile"
                  />
                </div>
                {/* F14: chip "Operadores" → bottom-sheet embutido no shared (< sm) */}
                <OperatorAvatarFilter
                  operators={users}
                  selectedId={selectedUserId === 'all' ? null : selectedUserId}
                  onSelect={(id) => setSelectedUserId(id ?? 'all')}
                  data-testid="attendance-operators-mobile"
                />
                <AttendanceFiltersComponent
                  variant="toolbar"
                  filters={filters}
                  onFiltersChange={setFilters}
                  channels={channelsForFilter}
                  teams={teamsForFilter}
                  providers={providersForFilter}
                />
              </div>
            </div>
          }
          below={
            (filters.channelId || filters.teamId || filters.providerId || filters.assigneeId) ? (
              <div
                className="flex-none flex items-center flex-wrap gap-2 px-4 py-2"
                style={{
                  backgroundColor: 'var(--theme-header-background)',
                  borderBottom: '1px solid var(--theme-header-border)',
                }}
                data-testid="attendance-filterbar"
              >
                <AttendanceFiltersComponent
                  variant="chips"
                  filters={filters}
                  onFiltersChange={setFilters}
                  channels={channelsForFilter}
                  teams={teamsForFilter}
                  providers={providersForFilter}
                />
              </div>
            ) : undefined
          }
        />

        {/* Kanban Board */}
        <div className="flex-1 overflow-auto">
          {error && (
            <div className="flex items-center justify-center h-full">
              <div
                className="px-4 py-3 rounded-lg border"
                style={{
                  backgroundColor: 'var(--theme-error-background)',
                  borderColor: 'var(--theme-error-border)',
                  color: 'var(--theme-error-text)'
                }}
              >
                Erro ao carregar conversas. Tente novamente.
              </div>
            </div>
          )}

          {!error && (
            <KanbanBoard
              columns={kanbanColumns}
              density={cardDensity}
              renderCard={(card: ConversationKanbanCard) => (
                <ConversationCardImproved
                  conversation={card}
                  onAction={handleAction}
                  showOperator={selectedUserId === 'all'}
                  providerId={card.providerType}
                />
              )}
              enableDragDrop={true}
              onCardMove={handleCardMove}
              onLoadMore={loadMore}
              loading={isLoading}
              emptyMessage="Nenhuma conversa encontrada"
              mobileTabStyle="lane-chip"
              defaultMobileColumnId="queue"
            />
          )}
        </div>
      </div>

      {/* Finalizar conversa (D8) — reuso do dialog do chat (desacoplado):
          motivo + observações, POST /conversations/:id/close via mutation
          existente de useConversationActions */}
      <FinishChatDialog
        // key remonta o dialog por conversa — sem isso, motivo/notas da
        // conversa anterior vazariam pré-selecionados na próxima (minor T6)
        key={actionTarget?.conversation.id ?? 'finish-dialog'}
        conversationId={actionTarget?.conversation.id ?? ''}
        conversationSubject={
          actionTarget?.conversation.contact?.name || actionTarget?.conversation.subject
        }
        isOpen={actionTarget?.action === 'close'}
        onClose={() => setActionTarget(null)}
        onFinish={(reason, notes) => closeConversation({ reason, notes })}
        isLoading={isClosing}
      />

      {/* Reabrir conversa (D8) — confirmação simples; POST /:id/reopen → waiting */}
      <Modal
        isOpen={actionTarget?.action === 'reopen'}
        onClose={() => setActionTarget(null)}
        maxWidth="sm"
        data-testid="attendance-reopen-modal"
      >
        <ModalHeader title="Reabrir conversa" onClose={() => setActionTarget(null)} />
        <ModalContent>
          <p className="text-sm" style={{ color: '#475569' }}>
            {`Reabrir a conversa${
              actionTarget?.conversation.contact?.name
                ? ` de ${actionTarget.conversation.contact.name}`
                : ''
            }? Ela voltará para a fila de espera.`}
          </p>
        </ModalContent>
        <ModalFooter>
          <Button
            data-testid="attendance-reopen-cancel"
            variant="secondary"
            size="md"
            onClick={() => setActionTarget(null)}
            disabled={isReopening}
          >
            Cancelar
          </Button>
          <Button
            data-testid="attendance-reopen-confirm"
            variant="primary"
            size="md"
            loading={isReopening}
            disabled={isReopening}
            onClick={() => reopenConversation()}
          >
            Reabrir
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
